import { Injectable } from '@angular/core';
import { DestroyableDirective } from '@app/shared/directives/destroyable';
import {
  BehaviorSubject,
  Subscription,
  from,
  of,
} from 'rxjs';
import { IMyCV } from '../data-structures/my-cvs.interface';
import {
  ResponseEnvelopeOfResumeGetAllResponse,
  ResponseEnvelopeOfResumeDuplicateResponse,
  ResponseEnvelopeOfResumeRemoveResponse,
  ResumeDuplicateRequest,
  ResponseEnvelopeOfResumeUnshareResponse,
  ResponseEnvelopeOfResumeShareResponse,
} from '@app/core/data-access/api';
import {
  takeUntil,
  toArray,
  first,
  mergeMap, tap
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ResumeService } from '@app/core/services/resume.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class MyCvsFacade extends DestroyableDirective {
  myCompletedCvs$ = new BehaviorSubject<IMyCV[]>([]);
  myDraftCvs$ = new BehaviorSubject<IMyCV[]>([]);

  pollingSubscription: Subscription;

  constructor(
    private translateService: TranslateService,
    private resumeService: ResumeService,
    private snackBar: MatSnackBar
  ) {
    super();
  }

  loadMyCvs() {
    const loadThumbnails = (myCvs: IMyCV[]) => {
      return from(myCvs).pipe(
        tap((cv: IMyCV) => {
          return of({ ...cv, thumbnail: cv.thumbnailUri ? cv.thumbnailUri : null });
        }),
        toArray()
      );
    };

    this.resumeService
      .getResumes()
      .pipe(
        first(),
        mergeMap((getAllResumesResponse: ResponseEnvelopeOfResumeGetAllResponse) => {
          if (!!getAllResumesResponse?.data?.templates) {
            return loadThumbnails(
              getAllResumesResponse?.data?.templates as IMyCV[]
            );
          }
        })
      )
      .subscribe((cvs: any) => {
        cvs.forEach(async cv => {
          if (cv.thumbnail) {
            cv.thumbnail = await this.readFile(cv.thumbnail);
          }
        });

        const completed = cvs.filter((t) => t.isCompleted);
        this.myCompletedCvs$.next(completed);

        const draft = cvs.filter((t) => !t.isCompleted);
        this.myDraftCvs$.next(draft);
      });
  }

  downloadResume(id: string, name: string): void {
    this.resumeService
      .downloadResume(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((resume: Blob) => {
        this.resumeService.saveResumeFile(resume, name);
      });
  }

  duplicateCv(id: string, title: string) {
    const body = new ResumeDuplicateRequest(
      ResumeDuplicateRequest.fromJS({
        title,
      })
    );
    this.resumeService
      .duplicateResume(id, body)
      .subscribe(
        (
          duplicateResumeResponse: ResponseEnvelopeOfResumeDuplicateResponse
        ) => {
          if (!!duplicateResumeResponse?.data?.identifier) {
            this.loadMyCvs();
          }
        }
      );
  }

  deleteCv(id: string) {
    this.resumeService
      .deleteResume(id)
      .subscribe(
        (removeResumeResponse: ResponseEnvelopeOfResumeRemoveResponse) => {
          if (!!removeResumeResponse?.data?.identifier) {
            this.loadMyCvs();
          }
        }, (err) => {
          if (err.error.messages.some(message =>
            message.code.toLocaleLowerCase() === ('@ResumeDeleteResumeInvalidResume').toLocaleLowerCase())) {
            this.snackBar.open(this.translateService.instant('tooltips.my_cv_list.disable_remove_default_cv'), null, {
              verticalPosition: 'top',
              panelClass: ['snackbar', 'snackbar-primary'],
              duration: 3000
            });
          }
        }
      );
  }

  share(id: string) {
    this.resumeService
      .shareResume(id)
      .subscribe((shareResponse: ResponseEnvelopeOfResumeShareResponse) => {
        if (!!shareResponse.data.secretKey) {
          this.loadMyCvs();
        }
      });
  }

  unshare(id: string) {
    this.resumeService
      .unshareResume(id)
      .subscribe((unshareResponse: ResponseEnvelopeOfResumeUnshareResponse) => {
        if (!!unshareResponse.data.identifier) {
          this.loadMyCvs();
        }
      });
  }

  readFile(file) {
    return new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        resolve(fileReader.result.toString());
      };
      fileReader.readAsDataURL(file);
    });
  }
}
